import React, { FC, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  BaseLevels,
  BaseSizes,
  Button,
  ButtonVariant,
  ExtraLevels,
  Heading,
  Icon,
  IconName,
  Input,
  InputType,
  Modal,
  Text,
  TextVariant,
  TextWeight,
} from '@kb-front/ui-kit';

import { EAlignItems, EJustifyContent, Panel } from '@kapital-bank/kb-ui-design-system';

import { ELang } from 'shared/enum';
import { IInsurance, ISelectedInsurance, ITariff } from 'shared/model';
import { translate } from 'shared/utils/translation';

import './index.scss';

interface IProps {
  insuranceListForPossibleMonths: IInsurance[];
  activeMonthPeriod?: string;
  wantedAmount: number;
  setIsVisible: (value: boolean) => void;
  setSelectedInsuranceData: (value: ISelectedInsurance) => void;
  selectedInsuranceData: ISelectedInsurance | null;
  hasActiveInsurance?: number;
}

const InsuranceList: FC<IProps> = ({
  setIsVisible,
  setSelectedInsuranceData,
  insuranceListForPossibleMonths,
  activeMonthPeriod,
  wantedAmount,
  selectedInsuranceData,
  hasActiveInsurance,
}) => {
  const { lang } = useParams();

  const defaultOption: ISelectedInsurance = {
    amount: null,
    checked: true,
    id: 100,
    imageName: '',
    label: '',
    month: '',
    name: 'selectedInsurance',
    value: 'insuranceNull',
    title: <Text level={BaseLevels.L2}>{translate('lifeInsurance.modal.donotNeed')}</Text>,
    percent: null,
  };
  const [selectedInsurance, setSelectedInsurance] = useState(hasActiveInsurance ? selectedInsuranceData : defaultOption);

  const updatedInsuranceListForPossibleMonths: ISelectedInsurance[] = useMemo(() => {
    return [
      {
        ...defaultOption,
        checked: selectedInsurance?.value === defaultOption.value, // Dynamically set checked
      },
      ...insuranceListForPossibleMonths.map((insuranceItem: IInsurance) => {
        const insuranceItemPercent = Number(
          insuranceItem?.tariffs.find((tarif: ITariff) => tarif.duration === Number(activeMonthPeriod))?.tariff?.toFixed(2)
        );
        const insuranceItemMinInsuranceFee = insuranceItem?.minInsuranceFee;
        const insuranceItemAmount = (wantedAmount * insuranceItemPercent) / 100;
        const insuranceItemValue = (insuranceItem.name?.split(' ')[0] || '').toLowerCase();
        let adjustedAmount = insuranceItemAmount;

        if (insuranceItemMinInsuranceFee && insuranceItemAmount < insuranceItemMinInsuranceFee) {
          adjustedAmount = insuranceItemMinInsuranceFee;
        }

        const updateInsuranceItemAmount = Number(adjustedAmount.toFixed(2));

        return {
          name: 'selectedInsurance',
          label: '',
          value: insuranceItemValue,
          checked: selectedInsurance?.value === insuranceItemValue,
          imageName: insuranceItem?.icon,
          title: insuranceItem?.name,
          amount: updateInsuranceItemAmount,
          percent: insuranceItemPercent,
          id: insuranceItem.id,
          month: activeMonthPeriod,
        };
      }),
    ];
  }, [activeMonthPeriod, selectedInsurance, wantedAmount, insuranceListForPossibleMonths, selectedInsuranceData]);

  const handleInsurance = useCallback(() => {
    setIsVisible(false);
    if (selectedInsurance) setTimeout(() => setSelectedInsuranceData(selectedInsurance), 80);
  }, [activeMonthPeriod, selectedInsurance]);

  return (
    <Modal
      onClose={() => setIsVisible(false)}
      visible
      size={BaseSizes.M}
      header={
        <Heading level={ExtraLevels.L5}>
          <div className="insurance-list__title">{translate('lifeInsurance.modal.title')}</div>
        </Heading>
      }
    >
      <Panel className="insurance-list">
        {updatedInsuranceListForPossibleMonths?.map((insuranceItem: ISelectedInsurance) => (
          <Panel className="insurance-list__item" key={insuranceItem.id}>
            <Panel alignItems={EAlignItems.CENTER} justifyContent={EJustifyContent.SPACE_BETWEEN} marginBottom={4} marginTop={4}>
              <Panel alignItems={EAlignItems.CENTER}>
                <Panel marginRight={1}>
                  {insuranceItem?.imageName && <img src={insuranceItem?.imageName} alt="3" className="insurance-list__item-img" />}
                </Panel>
                <Text level={BaseLevels.L2} block>
                  {insuranceItem?.title}
                </Text>
              </Panel>

              <Panel className="insurance-list__percent-details">
                {insuranceItem?.amount && (
                  <Panel>
                    <Text weight={TextWeight.SEMI_BOLD} block>
                      {insuranceItem?.amount} ₼
                    </Text>
                    {lang !== ELang.AZ ? (
                      <Text variant={TextVariant.SECONDARY} level={BaseLevels.L3}>
                        {insuranceItem.percent} {translate('lifeInsurance.modal.percentInfo')}
                      </Text>
                    ) : (
                      <Text variant={TextVariant.SECONDARY} level={BaseLevels.L3}>
                        {translate('lifeInsurance.modal.percentInfo')} {insuranceItem.percent}% - i
                      </Text>
                    )}
                  </Panel>
                )}
                <div className="insurance-list__radio-button">
                  <Input
                    type={InputType.RADIO}
                    label=""
                    name={insuranceItem?.name}
                    value={insuranceItem?.value}
                    checked={insuranceItem?.checked}
                    onChange={() => setSelectedInsurance(insuranceItem)}
                    key={insuranceItem?.name}
                  />
                </div>
              </Panel>
            </Panel>
          </Panel>
        ))}
        <Panel className="insurance-list__info" padding={3} alignItems={EAlignItems.CENTER} marginBottom={5} marginTop={3}>
          <Panel marginRight={1}>
            <Icon iconName={IconName.CIRCLE_INFO} size={BaseSizes.L} />
          </Panel>
          <Panel className="insurance-list__info-text">
            <Text level={BaseLevels.L3}>{translate('lifeInsurance.modal.info')}</Text>
          </Panel>
        </Panel>
      </Panel>

      <Button variant={ButtonVariant.PRIMARY} block onClick={handleInsurance}>
        {translate('lifeInsurance.modal.selectTarif')}
      </Button>
    </Modal>
  );
};

export default InsuranceList;
